import { ddLogger } from '@b-stock/fe-core/bstock-react'

import { reload } from '@helpers/windowLocation'

const RELOAD_ATTEMPTED_KEY = 'ap_chunk_reload_attempted'

type PreloadErrorEvent = Event & { payload?: unknown }

/**
 * One-shot guard for the automatic reload below; lives in sessionStorage
 * because it must survive a full page navigation. Mirrors
 * `src/app/helpers/auth/oauthChallenge.ts`'s `markReauthorizeAttempted`: the
 * write is read back rather than trusted, so a browser with storage
 * disabled is detected and never attempts the reload (which would
 * otherwise bounce forever, since the guard could never be observed as set).
 */
const hasAttemptedReload = (): boolean => {
  try {
    return sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === 'true'
  } catch {
    return false
  }
}

const markReloadAttempted = (): boolean => {
  try {
    sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, 'true')
    return sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === 'true'
  } catch {
    return false
  }
}

const clearReloadAttempted = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPTED_KEY)
  } catch {
    // Nothing we can do, and nothing depends on the removal succeeding.
  }
}

const toError = (payload: unknown): Error =>
  payload instanceof Error ? payload : new Error(String(payload))

/**
 * A page's lazy-loaded chunk can 404 when a tab is left open across a
 * deploy (the hashed filename it asks for no longer exists) or fail to
 * fetch outright. Vite dispatches `vite:preloadError` for both, once per
 * rejected dependency — a single failed navigation can fire it several
 * times (the chunk itself, its CSS, shared deps), all for the one page.
 */
export const setupChunkLoadRecovery = () => {
  // If a previous page load already used its one reload to get here, clear
  // the stored flag immediately: its job — surviving the navigation — is
  // done, and leaving it set would block a *later*, unrelated chunk failure
  // (e.g. after a future deploy) from getting its own reload. `recovered`
  // keeps that answer for the rest of *this* page's lifetime.
  const recovered = hasAttemptedReload()
  if (recovered) {
    clearReloadAttempted()
  }

  // Swallows every preloadError after the first on this page once a reload
  // is already underway, so sibling failures from the same navigation don't
  // reload twice or get logged as a fresh failure.
  let reloadInFlight = false

  const onPreloadError = (event: PreloadErrorEvent) => {
    if (reloadInFlight) {
      event.preventDefault()
      return
    }

    if (recovered) {
      ddLogger.error(
        'Page chunk failed to load after automatic reload',
        {},
        toError(event.payload)
      )
      return
    }

    if (!markReloadAttempted()) {
      ddLogger.error(
        'Page chunk failed to load; sessionStorage unavailable so no automatic reload was attempted',
        {},
        toError(event.payload)
      )
      return
    }

    reloadInFlight = true
    event.preventDefault()
    reload()
  }

  window.addEventListener('vite:preloadError', onPreloadError)

  return () => window.removeEventListener('vite:preloadError', onPreloadError)
}
